import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Icon } from '@/components/forge/Icon'
import { IconButton } from '@/components/forge/ForgeButton'
import { Chip } from '@/components/forge/Chip'
import { Mono, Num } from '@/components/forge/primitives'
import { AskOnSelection } from '@/components/forge/AskOnSelection'
import { useCopilotConversation } from '@/features/copilot/useCopilotConversation'
import { useCopilotAccess } from '@/features/copilot/access'
import { useRevealedText } from '@/features/copilot/useRevealedText'
import { Prose } from '@/components/forge/Prose'
import { queryKeys } from '@/services/queryClient'
import { cn } from '@/lib/cn'
import { CopilotChats } from './CopilotChats'
import { CopilotComposer } from './CopilotComposer'

/**
 * The assistant is docked, contextual and dismissible. It explains the data and
 * never competes with it — this is not a chat window with the plant behind it.
 *
 * Every answer here comes from the copilot service, which verifies each claim
 * against the records it read and strips anything unsupported before emitting it.
 * Nothing in this file invents, summarises or reformats an answer: the text that
 * arrives is the text that is shown, and the sources beneath it are what it rests
 * on.
 *
 * The dock is the one authored motion moment in the shell: a 500ms
 * exponential-ease width expansion with the panel sliding in from the inline
 * end. `will-change` and `contain` bound the reflow so a 360px animation does
 * not repaint the table beside it.
 *
 * ── One chat, everywhere ──────────────────────────────────────────────────
 * The dock is always mounted — closed is zero width, not unmounted — so the chat on
 * screen, and an answer still arriving, survive closing it and moving between
 * screens. The header switches to the list of earlier chats and starts a new one.
 * What a question is ABOUT comes from the screen underneath, as the composer's
 * context chip; see `features/copilot/pageContext`.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {{key: string, sessionId: string|null, title: string|null}} props.chat
 * @param {object} props.context `{route, module, recordId, label, prompts}` for the screen underneath
 * @param {{chatKey: string, token: string, message: string, action: object, route: string,
 *   analysing: string|null}|null} props.request a pending ForgeAI-button request,
 *   consumed once by the chat it names
 * @param {{chatKey: string, token: string, text: string}|null} props.quoted a passage
 *   to attach to the composer, consumed once by the chat it names
 * @param {boolean} props.busy an answer is being written
 */
export function AssistantDock({
  open,
  chat,
  context,
  request,
  quoted,
  busy,
  onNewChat,
  onOpenChat,
  onSession,
  onStatus,
  onAnalysing,
  onClose,
}) {
  const { t } = useTranslation(['app', 'common'])

  /* The list of chats replaces the transcript in place. Tied to the chat it was
     opened over, so picking one — or starting one — lands back on the chat. */
  const [listOver, setListOver] = useState(null)
  const showingChats = listOver === chat.key

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden [contain:layout_paint] [will-change:width]',
        '[transition:width_.5s_var(--ease-out-expo)]',
        // The dock is desktop and landscape-tablet only. On a narrow floor
        // tablet the data must keep the full width.
        'max-lg:!w-0',
      )}
      style={{ width: open ? 'var(--dock-w)' : 0 }}
      aria-hidden={!open}
    >
      <aside
        aria-label={t('dock.title')}
        className={cn(
          'flex h-full flex-col border-s border-hairline bg-card shadow-dock',
          'dark:backdrop-blur-[20px] dark:backdrop-saturate-[1.8]',
          open && 'animate-dock-in',
        )}
        style={{ width: 'var(--dock-w)' }}
      >
        <div className="flex items-center gap-1.5 border-b border-hairline py-3 ps-4 pe-3">
          <span className="me-1 flex size-6.5 shrink-0 items-center justify-center rounded-[7px] tint-violet">
            <Icon name="ai" size="sm" className="text-violet" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-control">{showingChats ? t('dock.chats.showChats') : t('dock.title')}</div>
            {showingChats ? null : (
              <div className="truncate text-micro font-normal text-ink-dim" title={chat.title ?? undefined}>
                {chat.title || t('dock.chats.newChat')}
              </div>
            )}
          </div>
          <IconButton
            bare
            label={showingChats ? t('dock.chats.back') : t('dock.chats.showChats')}
            pressed={showingChats}
            onClick={() => setListOver(showingChats ? null : chat.key)}
            className={cn('size-7', showingChats && 'text-ink')}
          >
            <Icon name="list" size="sm" />
          </IconButton>
          {/* Disabled while an answer is being written: a new chat remounts the
              conversation, which would cut the answer off mid-stream. */}
          <IconButton
            bare
            label={t('dock.chats.newChat')}
            disabled={busy}
            onClick={onNewChat}
            className="size-7 disabled:pointer-events-none disabled:opacity-40"
          >
            <Icon name="plus" size="sm" />
          </IconButton>
          <IconButton bare label={t('dock.close')} onClick={onClose} className="size-7">
            <Icon name="x" size="sm" />
          </IconButton>
        </div>

        {showingChats ? (
          <CopilotChats
            currentId={chat.sessionId}
            busy={busy}
            onPick={(session) => {
              if (session.id === chat.sessionId) setListOver(null)
              else onOpenChat(session)
            }}
          />
        ) : null}

        {/* Hidden rather than unmounted behind the list, so an answer still
            arriving keeps arriving. Keyed by the chat: a new or reopened chat is a
            new component, never a reset that could show one frame of the last. */}
        <div className={cn('flex min-h-0 flex-1 flex-col', showingChats && 'hidden')}>
          <Conversation
            key={chat.key}
            chat={chat}
            context={context}
            request={request}
            quoted={quoted}
            onSession={onSession}
            onStatus={onStatus}
            onAnalysing={onAnalysing}
            onNewChat={onNewChat}
          />
        </div>
      </aside>
    </div>
  )
}

/** `#ANM-234 What happened?` — the context chip in front of the text, when there is one. */
const withChip = (chip, text) => (chip ? `${chip} ${text}` : text)

/**
 * What a successful action changed, as the cache keys to refetch.
 *
 * Matched on the module rather than the action type: the type vocabulary belongs
 * to the engine and will grow, while the module is the exact granularity of what
 * is being invalidated.
 */
const INVALIDATES = {
  anomalies: (id) => [queryKeys.anomaliesList, ...(id ? [queryKeys.anomaly(id)] : [])],
  work_orders: () => [['work-orders']],
  logbook: () => [['logbook']],
  shift_handovers: () => [queryKeys.shiftHandovers],
}

function Conversation({ chat, context, request, quoted, onSession, onStatus, onAnalysing, onNewChat }) {
  const { t } = useTranslation(['app', 'common'])
  const client = useQueryClient()
  const access = useCopilotAccess()

  /* The chat this instance was mounted for. Frozen: the first question creates the
     session and the shell records its id on the SAME chat, and reading that id
     here would fetch the transcript of the turn already on screen. */
  const [mountedSession] = useState(chat.sessionId)

  /**
   * The copilot writes, so the page behind the dock can go stale.
   *
   * Two sources, both deliberate. What the server's dispatcher ACTUALLY DID,
   * reported on the `done` frame. And a turn that merely ASKED for an analysis —
   * those results are reported once, never persisted, so a turn whose ending we
   * missed tells us nothing; one cheap refetch beats an invisible stale page.
   *
   * The chats list always moves: every turn changes its session's `updated_at`.
   */
  const onTurnEnd = useCallback(
    ({ actions = [], analysed }) => {
      const keys = [queryKeys.copilotSessions]
      if (analysed) keys.push(...INVALIDATES.anomalies(analysed))
      for (const action of actions) {
        if (action?.status !== 'succeeded') continue
        keys.push(...(INVALIDATES[action.module]?.(action.module_id) ?? []))
      }
      for (const queryKey of keys) client.invalidateQueries({ queryKey })
    },
    [client],
  )

  const { messages, ask, cancel, isStreaming, analysingId, blocked, isRestoring, restoreFailed } =
    useCopilotConversation({ sessionId: mountedSession }, { onSession, onTurnEnd })

  /* Up to the shell: trigger buttons disable while an answer is being written, and
     a trigger aimed at a chat that cannot take a question starts a fresh one. */
  useEffect(() => {
    onStatus?.({ busy: isStreaming, blocked: blocked !== 'none' })
  }, [isStreaming, blocked, onStatus])
  useEffect(() => () => onStatus?.({ busy: false, blocked: false }), [onStatus])

  /**
   * Tell the shell when an analysis is being written, and for which record.
   *
   * It reports the TURN, not the analysis: the server offers no progress to poll,
   * so the radar means "this is arriving here, now". Recorded in BACKEND-GAPS.md
   * against a request for a progress signal.
   */
  useEffect(() => {
    onAnalysing?.(analysingId)
  }, [analysingId, onAnalysing])
  useEffect(() => () => onAnalysing?.(null), [onAnalysing])

  const canAsk = access.granted && !isStreaming && blocked === 'none' && !isRestoring

  /**
   * Follow the answer down as it arrives — but stop the moment the reader takes
   * over: it pins to the bottom only while it is already at the bottom.
   */
  const scroller = useRef(null)
  const pinned = useRef(true)

  const onScroll = () => {
    const node = scroller.current
    if (!node) return
    pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24
  }

  useEffect(() => {
    const node = scroller.current
    if (!node || !pinned.current) return
    node.scrollTop = node.scrollHeight
  }, [messages])

  /**
   * The composer's state that belongs to the SCREEN: the quoted passages and
   * whether the context chip was removed.
   *
   * Reset when the screen changes — the chip switches to the new screen's context
   * and the field is cleared (the composer itself is keyed by the route). Adjusted
   * during render rather than from an effect, so no frame shows the new screen's
   * chip beside the old screen's quotes.
   */
  const [compose, setCompose] = useState({ route: context.route, citations: [], dismissed: false })
  if (compose.route !== context.route) {
    setCompose({ route: context.route, citations: [], dismissed: false })
  }
  const citations = compose.route === context.route ? compose.citations : []
  const input = useRef(null)
  const quotedRef = useRef(null)

  const setCitations = (update) =>
    setCompose((current) => ({ ...current, citations: update(current.citations) }))
  const removeCitation = (id) => setCitations((current) => current.filter((entry) => entry.id !== id))

  /** Attach a passage the reader selected inside the transcript itself. */
  const addCitation = useCallback((text) => {
    setCompose((current) => ({
      ...current,
      citations: [...current.citations, { id: crypto.randomUUID(), text }],
    }))
    input.current?.focus()
  }, [])

  /* A passage quoted elsewhere on the screen. The shell holds ONE pending quote
     and re-stamps it, so the token is what says "this is a new passage". */
  useEffect(() => {
    if (!quoted || quoted.chatKey !== chat.key || quotedRef.current === quoted.token) return
    quotedRef.current = quoted.token
    setCompose((current) => ({
      ...current,
      citations: [...current.citations, { id: quoted.token, text: quoted.text }],
    }))
    input.current?.focus()
  }, [quoted, chat.key])

  /**
   * The context chip and the mention it stands for.
   *
   * On a detail screen the id is known from the path before the record has loaded,
   * so the mention is complete at once and only the chip's name waits — `#…` until
   * the screen reports `#ANM-234`.
   */
  const contextMention =
    context.module && !compose.dismissed ? { module: context.module, module_id: context.recordId ?? null } : null
  const contextLabel = contextMention
    ? context.recordId
      ? `#${context.label ?? '…'}`
      : `@${t(`dock.modules.${context.module}`)}`
    : null
  /* The chip is also written at the start of the message, so the text alone says
     what it is about. Left off while a record's name has not arrived — `#…` is a
     placeholder, not a name. */
  const contextText = contextMention && (!context.recordId || context.label) ? contextLabel : null

  /**
   * Consume a pending ForgeAI-button request.
   *
   * Guarded on the token, so pressing the same button twice fires twice while a
   * re-render fires nothing. No mentions: a button types no `@` or `#`, and the
   * action's `module_id` is what names the record.
   */
  const consumed = useRef(null)
  useEffect(() => {
    if (!request || request.chatKey !== chat.key) return
    if (consumed.current === request.token) return
    if (isRestoring) return
    consumed.current = request.token
    ask(withChip(request.chip === undefined ? contextText : request.chip, request.message), {
      mentions: [],
      route: request.route,
      actions: [request.action],
      analysing: request.analysing,
    })
  }, [request, chat.key, isRestoring, ask, contextText])

  /* Forget what was consumed when torn down. StrictMode's mount → cleanup → mount
     aborts the first ask; without this the second pass would skip it, and the
     panel would read "Working…" with nothing behind it for good. */
  useEffect(
    () => () => {
      consumed.current = null
    },
    [],
  )

  /**
   * Send a question, with the citations in front of it when there are any.
   *
   * The quotes go into the MESSAGE, because the copilot has one channel and this is
   * it. The mentions are the context chip plus anything typed with `@` or `#`.
   */
  const send = (question, typed = []) => {
    if (!canAsk || !question.trim()) return
    const asked = withChip(contextText, question)
    ask(
      citations.length
        ? t('dock.quotedAsk', {
            count: citations.length,
            quotes: citations.map((entry) => t('dock.quoted.text', { quote: entry.text })).join('\n'),
            question: asked,
          })
        : asked,
      {
        mentions: [...(contextMention ? [contextMention] : []), ...typed],
        route: context.route,
      },
    )
    setCitations(() => [])
  }

  const notice = restoreFailed ? t('dock.restoreFailed') : blocked !== 'none' ? t(`dock.blocked.${blocked}`) : null

  return (
    <>
      {/* Selection inside the transcript, scoped to the answers — see `AskOnSelection`. */}
      <AskOnSelection containerRef={scroller} within="[data-quotable]" onAsk={addCitation} disabled={!canAsk} />

      <div ref={scroller} onScroll={onScroll} className="flex-1 overflow-y-auto p-4" aria-live="polite">
        {!access.isPending && !access.granted ? (
          <p className="text-body-sm text-ink-dim">{t('dock.forbidden')}</p>
        ) : messages.length === 0 && isRestoring ? (
          <p className="text-body-sm text-ink-dim">{t('dock.restoring')}</p>
        ) : messages.length === 0 && !notice ? (
          <p className="text-body-sm text-ink-dim">{t('dock.empty')}</p>
        ) : (
          <div className="flex flex-col gap-3.5">
            {messages.map((message, position) =>
              message.role === 'user' ? (
                <div key={message.id} className="ms-auto max-w-[86%]">
                  <p className="rounded-[12px_12px_2px_12px] border border-hairline bg-sunken px-3 py-2.5 text-body-sm">
                    {message.text}
                  </p>
                  {/* A replayed question the server marked `error` has no answer and
                      never will; unmarked it reads as a question the copilot ignored. */}
                  {message.failed ? (
                    <p className="mt-1 text-end text-micro font-normal text-danger">{t('dock.failed')}</p>
                  ) : null}
                </div>
              ) : (
                <AssistantTurn
                  key={message.id}
                  message={message}
                  // Only the last turn can still be arriving, and only while the
                  // connection is open.
                  live={isStreaming && position === messages.length - 1}
                />
              ),
            )}
          </div>
        )}

        {/* Why this chat cannot take a question, and the way out. Said rather than
            left as a dead field: the reason is one the reader wants anyway. */}
        {notice ? (
          <div className="mt-3.5 rounded-sm border border-hairline bg-sunken p-3">
            <p className="m-0 text-body-sm text-ink-dim">{notice}</p>
            <button
              type="button"
              className="mt-2 text-body-sm text-ink underline underline-offset-2"
              onClick={onNewChat}
            >
              {t('dock.chats.newChat')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="border-t border-hairline px-4 pt-3 pb-5">
        {/* A turn can run for minutes and the composer is shut for all of it.
            Cancelling stops the reader; the turn goes on being written. */}
        {isStreaming ? (
          <p className="mb-2.5 flex items-start gap-1.5 text-micro font-normal text-ink-dim">
            <Icon name="ai" size="xs" className="mt-0.5 shrink-0 text-violet" />
            <span className="flex-1">{t('dock.analysing')}</span>
            <button type="button" className="shrink-0 underline underline-offset-2" onClick={cancel}>
              {t('dock.cancel')}
            </button>
          </p>
        ) : null}

        {/* The screen's suggested questions. Hidden once a passage is attached:
            somebody who has highlighted a clause has decided what to ask. */}
        {citations.length ? null : (
          <div className="flex gap-1.5 overflow-x-auto pb-2.5">
            {context.prompts.map((key) => (
              <Chip
                key={key}
                className="shrink-0 disabled:pointer-events-none disabled:opacity-40"
                disabled={!canAsk}
                onClick={() => send(t(`dock.prompts.${key}`))}
              >
                {t(`dock.prompts.${key}`)}
              </Chip>
            ))}
          </div>
        )}

        <CopilotComposer
          key={context.route}
          inputRef={input}
          disabled={!canAsk}
          placeholder={t('dock.askPlaceholder')}
          contextLabel={contextLabel}
          onClearContext={() => setCompose((current) => ({ ...current, dismissed: true }))}
          onSend={send}
          attachments={
            citations.length ? (
              <Citations citations={citations} onRemove={removeCitation} />
            ) : null
          }
        />
      </div>
    </>
  )
}

/**
 * The quoted passages, as cards.
 *
 * Clamped to six lines by CSS with the full text on `title`; scrolls sideways
 * rather than wrapping (which would eat the transcript) or capping (which would
 * silently refuse a quote). The scrollbar is hidden and a count says how many are
 * attached instead — a fade would be a gradient, which DESIGN.md forbids.
 */
function Citations({ citations, onRemove }) {
  const { t } = useTranslation(['app', 'common'])
  return (
    <ul aria-label={t('dock.quoted.label')} className="scrollbar-none m-0 flex list-none gap-2 overflow-x-auto p-2.5">
      {citations.map((entry) => (
        <li key={entry.id} className="relative shrink-0">
          {/* The rule is the blockquote convention — neutral, never violet. Violet
              is reserved for text the machine authored. */}
          <div className="flex w-[152px] gap-2 rounded-xs border border-subtle bg-card p-2">
            <span aria-hidden="true" className="w-[3px] shrink-0 self-stretch rounded-pill bg-ink-mute" />
            <p className="m-0 line-clamp-6 h-[6lh] min-w-0 flex-1 text-micro font-normal text-ink-dim" title={entry.text}>
              {entry.text}
            </p>
          </div>
          {/* Always visible rather than on hover: there is no hover on a floor tablet. */}
          <IconButton
            bare
            label={t('dock.quoted.clear')}
            onClick={() => onRemove(entry.id)}
            className={cn(
              'absolute -top-1.5 -start-1.5 size-5 rounded-pill max-lg:size-6',
              'border border-hairline bg-card shadow-edge',
              'opacity-75 transition-opacity hover:opacity-100',
            )}
          >
            <Icon name="x" size="xs" />
          </IconButton>
        </li>
      ))}

      {citations.length > 1 ? (
        <li
          aria-hidden="true"
          className="sticky -end-2.5 z-10 flex shrink-0 items-center bg-page ps-1.5"
          title={t('dock.quoted.count', { count: citations.length })}
        >
          <span className="num rounded-pill border border-subtle bg-sunken px-1.5 py-0.5 text-micro font-normal text-ink-dim">
            {citations.length}
          </span>
        </li>
      ) : null}
    </ul>
  )
}

/**
 * One assistant turn: what it is doing, what it answered, what that rests on.
 *
 * The sources are not decoration. The service strips any claim it cannot tie to a
 * record, so the list underneath is the difference between an answer and an
 * assertion — hiding it would throw away the only thing that makes the text worth
 * believing.
 */
/* Up to this many sources read at a glance; beyond it they are a wall. Answers on
   an anomaly's own page cite one or two, so the common case is unchanged. */
const INLINE_SOURCES = 3

function AssistantTurn({ message, live = false }) {
  const { t } = useTranslation(['app', 'common'])

  /**
   * The engine emits one delta per verified sentence, so the raw text grows in
   * sentence-sized jumps — readable, but it reads as blocks appearing rather than
   * as something being written. This paces those jumps into characters.
   *
   * It only ever shows a prefix of what actually arrived. The pacing is the whole
   * of what is added; no character on screen is one the verifier had not already
   * passed.
   */
  const { text, catchingUp } = useRevealedText(message.text ?? '', live, message.replayed)

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-micro text-on-violet">
        <Icon name="ai" size="sm" />
        {t('common:ai.authored')}
      </div>

      {/* ── What it is doing, step by step ─────────────────────────────
          The engine reports every tool call it opens and closes, and this is the
          only place a reader can see that the answer came from somewhere. It
          replaces a single "Reading {tool}…" line, which could hold one name at a
          time and so could not describe a turn that resolves an entity and searches
          anomalies at once — which is most of them.

          Shown only while the turn is live. These are the THINKING, and once there
          is an answer the durable record of where it came from is `sources` below:
          a permanent list of tool calls under every past reply would be the panel
          keeping its own workings on screen after they stopped being the news.

          The names are the engine's own identifiers, so they are monospaced —
          DESIGN.md reserves that for machine strings, and inventing friendly labels
          for them here would be this file writing copy about a vocabulary the
          server owns. */}
      {live && message.stages?.length ? (
        <ul aria-label={t('dock.stages')} className="m-0 mb-2 flex list-none flex-col gap-1">
          {message.stages.map((stage) => (
            /* Keyed by the engine's own step id, which is stable across the frames
               that open and close a step — they are the same item, merged, not two.
               Keyed by position it would be a different row each time a step gained
               its duration. */
            <li key={stage.id} className="flex items-center gap-1.5 text-micro">
              {stage.done ? (
                <Icon name="check" size="xs" aria-hidden="true" className="shrink-0 text-violet" />
              ) : (
                /* Running. The same pulsing dot the severity band uses for a live
                   thing, at the size a list item can afford. */
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full bg-violet motion-safe:animate-pulse"
                />
              )}
              <Mono className="truncate text-ink-dim">{stage.name}</Mono>
              {/* Tabular, because these are a column of comparable figures even
                  when the column is two rows long.

                  Finished WITHOUT a figure is a real third state, not an oversight.
                  A tool call reports how long it took; the analysis step reports
                  only that it started, and is never updated again — so the turn's
                  own ending is what closes it, and there is no duration to print.
                  Printing `0ms` against six minutes of work would be worse than
                  printing nothing. */}
              {stage.ms == null ? null : (
                <Num className="ms-auto shrink-0 text-ink-mute">
                  {t('dock.stageMs', { ms: stage.ms })}
                </Num>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {/* What happened to the conversation, when something did. The panel says it
          rather than letting an answer appear mid-thought, or a thread restart, with
          no account of why. */}
      {message.notice ? (
        <p className="mb-2 text-micro font-normal text-ink-mute">
          {t(`dock.notice.${message.notice}`)}
        </p>
      ) : null}

      {text ? (
        /* `data-quotable` is what `AskOnSelection` in the transcript scopes to.
           On the ANSWER alone, not on the turn: the provenance list, the "Reading
           {tool}…" line and the disambiguation chips around it are this panel's own
           furniture, and a quote pulled out of them would be the copilot being asked
           to explain its own interface. */
        <div data-quotable className="text-body-sm leading-relaxed">
          {/* An analysis arrives as Markdown — a heading in bold and a numbered list
              of causes — because the engine writes one string for every reader it
              has. Rendered as plain text it prints its own asterisks, on the single
              most important answer this product gives. `Prose` handles exactly what
              the engine emits and nothing else. */}
          <Prose text={text} />
          {/* The caret says "there is more coming", which is the one thing a
              half-written answer has to communicate. It is dropped the instant
              nothing more is arriving, so a finished answer never looks live. */}
          {catchingUp ? (
            <span
              aria-hidden="true"
              className="ms-0.5 inline-block h-[1em] w-[2px] translate-y-[0.15em] bg-ink-dim motion-safe:animate-pulse"
            />
          ) : null}
        </div>
      ) : !message.error ? (
        /* Still said when stages are on screen above it: a finished stage list with
           no answer under it looks like a turn that stopped, and the one thing this
           line exists to say is that something is still coming. */
        <p className="text-body-sm italic text-ink-dim">{t('dock.thinking')}</p>
      ) : null}

      {message.error ? (
        <p className="mt-2 text-body-sm text-danger">{t('dock.failed')}</p>
      ) : null}

      {/* ── The disambiguation chips are GONE, and not by choice here ──
          The engine still produces them and deliberately does not send them: the
          chunk protocol has no field for a choice, so it counts them and logs that
          they went nowhere. A row that can never populate is dead UI, and one that
          renders "Which did you mean?" above nothing is worse. Recorded in
          BACKEND-GAPS.md against a request for somewhere to put them. */}

      {/* Folded once there are more than a handful.

          The sources are not decoration — the service strips any claim it cannot tie
          to a record, so this list is the difference between an answer and an
          assertion, and it does not get removed. But an answer that read ten
          anomalies printed ten full titles under two lines of prose, which buries
          the answer under its own footnotes and is worst exactly where those records
          are already on screen behind the dock.

          So: a count, always visible, and the records one click away. Few enough to
          read at a glance and it opens itself, which keeps the common case — one or
          two records on an anomaly's own page — exactly as it was. `details` rather
          than a button, so it is keyboard-operable and expandable without state. */}
      {/* `> 0` rather than the length itself: `sources` is seeded as an empty array,
          and `[] .length && …` is `0`, which React renders as a literal 0 under every
          answer that cited nothing. */}
      {message.sources?.length > 0 && !catchingUp ? (
        <details className="mt-3" open={message.sources.length <= INLINE_SOURCES}>
          <summary className="mb-1.5 cursor-pointer text-micro uppercase tracking-wide text-ink-dim marker:text-ink-dim">
            {message.sources.length <= INLINE_SOURCES
              ? t('dock.sources')
              : t('dock.sourcesCount', { count: message.sources.length })}
          </summary>
          <ul className="flex flex-col gap-1">
            {/* `{module, module_id, title}` — the server's vocabulary, not the
                engine's internal one. Deliberately not links: `module` mixes
                addressable records (anomalies, documents, assets) with reasoning
                artefacts that have no page to open, and a citation that navigates
                nowhere half the time is worse than one that never claims to. */}
            {message.sources.map((source) => (
              <li
                key={`${source.module}-${source.module_id}`}
                className="truncate text-micro font-normal text-ink-dim"
                title={source.module_id}
              >
                {source.title || source.module_id}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {/* ── What the turn changed, under what it rests on ──────────────────
          The dispatcher's own report from the `done` frame —
          `{module, type, module_id, status, reason}`, one entry per directive, in
          the same list whether it succeeded or not. A failed one stays on screen
          with the server's reason: an answer that says "I created the work order"
          over a write that was refused is the one thing this row exists to catch.

          Not links, for the same reason the sources are not: a `create` reports
          `module_id: null`, so the new record has no id here to open. */}
      {message.actions?.length > 0 && !catchingUp ? (
        <div className="mt-3">
          <p className="mb-1.5 text-micro uppercase tracking-wide text-ink-dim">
            {t('dock.actions')}
          </p>
          <ul className="flex flex-col gap-1">
            {message.actions.map((action, index) => {
              const failed = action.status !== 'succeeded'
              return (
                /* Positional key: a create carries no id, and two creates of the
                   same module in one turn are two rows. The list is replaced
                   whole, never reordered. */
                <li key={index} className="flex items-start gap-1.5 text-micro font-normal">
                  <Icon
                    name={failed ? 'x' : 'check'}
                    size="xs"
                    aria-hidden="true"
                    className={`mt-0.5 shrink-0 ${failed ? 'text-danger' : 'text-violet'}`}
                  />
                  <span className="min-w-0">
                    <span className="text-ink-dim">
                      {t('dock.action', { type: action.type, module: action.module })}
                    </span>
                    {failed ? (
                      <span className="block text-danger">
                        {t('dock.actionFailed')}
                        {action.reason ? ` — ${action.reason}` : null}
                      </span>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}

      {/* Both wait for the answer to settle. A disclaimer under half a sentence,
          or a source list that grows while the text above it is still being
          written, reads as the panel finishing before it has. */}
      {message.text && !catchingUp ? (
        <p className="mt-3.5 text-micro font-normal italic text-ink-dim">
          {t('common:ai.disclaimer')}
        </p>
      ) : null}
    </div>
  )
}
